import { Howler } from 'howler'

const STORAGE_KEY = 'trollilopolis:muted'

type MuteListener = (muted: boolean) => void

/**
 * Global mute state shared by SFX (Howler) and speech synthesis.
 * When muted, sounds are silenced but the game keeps its pacing via
 * short fixed dwells so scenes don't strobe past.
 */
class AudioSettings {
  private muted = false
  private listeners = new Set<MuteListener>()

  constructor() {
    try {
      this.muted = window.localStorage.getItem(STORAGE_KEY) === '1'
    } catch {
      this.muted = false
    }
    Howler.mute(this.muted)
  }

  public isMuted(): boolean {
    return this.muted
  }

  public setMuted(muted: boolean): void {
    this.muted = muted
    Howler.mute(muted)
    // Speech cancellation is handled by SpeechService, which subscribes to
    // this setting and releases any mid-speech line back to the queue.
    try {
      window.localStorage.setItem(STORAGE_KEY, muted ? '1' : '0')
    } catch {
      // localStorage unavailable — mute still applies for this session
    }
    this.listeners.forEach((listener) => listener(muted))
  }

  public toggle(): boolean {
    this.setMuted(!this.muted)
    return this.muted
  }

  public subscribe(listener: MuteListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
}

export const audioSettings = new AudioSettings()
